// Invoice / Receipt PDF builder using jsPDF.
// Produces true PDF (not printed HTML) for share + download + print.
//
// Supported paper sizes:
//   - "a4"    : A4 branded tax invoice with itemised table
//   - "80mm"  : 80mm thermal receipt (~ 72mm print width)
//   - "58mm"  : 58mm thermal receipt (~ 48mm print width)
//
// Usage:
//   const doc = buildInvoicePDF({ order, invoice, cfg, paperSize: "a4" });
//   const blob = doc.output("blob");
//   const dataUrl = doc.output("datauristring");
//   const filename = pdfFilename(order, invoice);

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const asMoney = (n) => `Rs. ${Math.round(Number(n || 0))}`;

export function pdfFilename(order, invoice) {
  const id = invoice?.invoice_number || order?.order_number || "invoice";
  return `${id}.pdf`.replace(/[^\w.\-]/g, "_");
}

/* ---------------- A4 branded invoice ---------------- */
function buildA4(order, invoice, cfg) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const M = 14;

  const storeName = cfg?.store_name || "Babu Baazar";
  const p = order.pricing || {};
  const addr = order.address || {};
  const cashier = (order.notes || "").split("cashier ")[1]?.split(" ")[0] || "-";
  const dt = new Date(order.created_at || Date.now()).toLocaleString("en-IN");
  const payMethod = (order.payment?.method || "cash").toUpperCase();

  // Brand bar
  doc.setFillColor(220, 30, 30);
  doc.rect(0, 0, pageW, 3, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(220, 30, 30);
  doc.text(storeName, M, 18);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const meta = [
    cfg?.address,
    cfg?.phone && `Ph: ${cfg.phone}`,
    cfg?.email,
    cfg?.gstin && `GSTIN: ${cfg.gstin}`,
  ].filter(Boolean).join(" | ");
  if (meta) doc.text(meta, M, 24, { maxWidth: pageW - 2 * M });

  // Right meta
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(17, 17, 17);
  doc.text("TAX INVOICE", pageW - M, 18, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(invoice?.invoice_number || order.order_number, pageW - M, 24, { align: "right" });
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(dt, pageW - M, 29, { align: "right" });

  // Payment pill (right)
  doc.setFillColor(220, 252, 231);
  doc.setDrawColor(220, 252, 231);
  doc.roundedRect(pageW - M - 26, 32, 26, 6, 3, 3, "F");
  doc.setTextColor(22, 101, 52);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(payMethod, pageW - M - 13, 36, { align: "center" });

  // Separator
  doc.setDrawColor(220, 30, 30);
  doc.setLineWidth(0.6);
  doc.line(M, 42, pageW - M, 42);

  // Billed-to / Order cards
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("BILLED TO", M, 50);
  doc.text("ORDER", M + (pageW - 2 * M) / 2, 50);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(17, 17, 17);
  doc.text(addr.name || "Walk-in customer", M, 56);
  if (addr.phone) {
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.text(String(addr.phone), M, 61);
  }

  doc.setFontSize(11);
  doc.setTextColor(17, 17, 17);
  doc.text(order.order_number, M + (pageW - 2 * M) / 2, 56);
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text(`Cashier: ${cashier}`, M + (pageW - 2 * M) / 2, 61);

  // Items table
  const rows = (order.items || []).map((it, i) => [
    String(i + 1),
    it.name,
    String(it.qty),
    asMoney(it.unit_price),
    asMoney(Number(it.qty) * Number(it.unit_price)),
  ]);

  autoTable(doc, {
    startY: 70,
    head: [["#", "Product", "Qty", "Rate", "Amount"]],
    body: rows,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 9, cellPadding: 2.5, textColor: [17, 17, 17] },
    headStyles: { fillColor: [254, 242, 242], textColor: [220, 30, 30], fontStyle: "bold", fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      2: { halign: "right", cellWidth: 16 },
      3: { halign: "right", cellWidth: 24 },
      4: { halign: "right", cellWidth: 26 },
    },
    margin: { left: M, right: M },
  });

  let y = doc.lastAutoTable.finalY + 6;
  const rightX = pageW - M;
  const gst = Number(p.tax || 0);
  const disc = Number(p.discount || p.extra_discount || 0);
  const sub = Number(p.subtotal || 0);
  const total = Number(p.total || 0);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text("Subtotal", rightX - 40, y, { align: "right" });
  doc.setTextColor(17, 17, 17);
  doc.text(asMoney(sub), rightX, y, { align: "right" });
  y += 5;

  if (disc > 0) {
    doc.setTextColor(90, 90, 90);
    doc.text("Extra Discount", rightX - 40, y, { align: "right" });
    doc.setTextColor(22, 101, 52);
    doc.text(`- ${asMoney(disc)}`, rightX, y, { align: "right" });
    y += 5;
  }

  doc.setTextColor(90, 90, 90);
  doc.text("GST (incl.)", rightX - 40, y, { align: "right" });
  doc.setTextColor(17, 17, 17);
  doc.text(asMoney(gst), rightX, y, { align: "right" });
  y += 8;

  // Grand total pill
  doc.setFillColor(220, 30, 30);
  doc.roundedRect(rightX - 70, y - 5, 70, 10, 1.5, 1.5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text("Grand Total", rightX - 68, y + 1.5);
  doc.text(asMoney(total), rightX - 2, y + 1.5, { align: "right" });

  // Footer
  y += 18;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  const thanks = cfg?.thank_you || "Thank you for shopping with us. Please visit again!";
  doc.text(thanks, pageW / 2, y, { align: "center" });
  if (cfg?.footer) {
    y += 5;
    doc.text(cfg.footer, pageW / 2, y, { align: "center" });
  }
  if (cfg?.terms) {
    y += 6;
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 140);
    doc.text(cfg.terms, pageW / 2, y, { align: "center", maxWidth: pageW - 2 * M });
  }

  return doc;
}

/* ---------------- Thermal receipt (58mm / 80mm) ---------------- */
function buildThermal(order, invoice, cfg, widthMM) {
  // Estimate page height from lines
  const items = order.items || [];
  const lineCount = items.length;
  const baseHeight = 90; // header + totals + footer
  const perLine = 10;
  const height = Math.max(120, baseHeight + lineCount * perLine + (cfg?.terms ? 20 : 0));

  const doc = new jsPDF({ unit: "mm", format: [widthMM, height] });
  const pad = widthMM >= 80 ? 4 : 3;
  const inner = widthMM - pad * 2;
  const centerX = widthMM / 2;
  const storeName = cfg?.store_name || "Babu Baazar";
  const p = order.pricing || {};
  const addr = order.address || {};
  const cashier = (order.notes || "").split("cashier ")[1]?.split(" ")[0] || "-";
  const dt = new Date(order.created_at || Date.now()).toLocaleString("en-IN");
  const payMethod = (order.payment?.method || "cash").toUpperCase();

  let y = 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(widthMM >= 80 ? 12 : 10);
  doc.setTextColor(0, 0, 0);
  doc.text(storeName, centerX, y, { align: "center" });
  y += 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  const showAddress = cfg?.show_address !== false;
  const showGst = cfg?.show_gst !== false;
  if (showAddress && cfg?.address) { doc.text(cfg.address, centerX, y, { align: "center", maxWidth: inner }); y += 3; }
  if (showAddress && cfg?.phone) { doc.text(`Ph: ${cfg.phone}`, centerX, y, { align: "center" }); y += 3; }
  if (showGst && cfg?.gstin) { doc.text(`GSTIN: ${cfg.gstin}`, centerX, y, { align: "center" }); y += 3; }
  y += 1;

  // dashed line
  const dashed = () => {
    doc.setLineDashPattern([1, 1], 0);
    doc.line(pad, y, widthMM - pad, y);
    doc.setLineDashPattern([], 0);
    y += 2.5;
  };
  dashed();

  doc.setFontSize(7);
  if (order.is_offline) {
    doc.setFont("helvetica", "bold");
    doc.text("*** OFFLINE BILL - PENDING SYNC ***", centerX, y, { align: "center" });
    y += 3;
    doc.setFont("helvetica", "normal");
  }
  doc.text(`Bill: ${invoice?.invoice_number || order.order_number}`, pad, y); y += 3;
  doc.text(`Date: ${dt}`, pad, y); y += 3;
  doc.text(`Cashier: ${cashier}`, pad, y); y += 3;
  if (addr.name || order.customer?.name) { doc.text(`Cust: ${addr.name || order.customer?.name}`, pad, y); y += 3; }
  dashed();

  // Items
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text("Item", pad, y);
  doc.text("Qty", widthMM - pad - 22, y, { align: "right" });
  doc.text("Rate", widthMM - pad - 12, y, { align: "right" });
  doc.text("Amt", widthMM - pad, y, { align: "right" });
  y += 3;
  dashed();
  doc.setFont("helvetica", "normal");
  for (const it of items) {
    const name = String(it.name || "");
    // wrap long names
    const lines = doc.splitTextToSize(name, inner - 26);
    doc.text(lines, pad, y);
    const amount = Number(it.qty) * Number(it.unit_price);
    doc.text(String(it.qty), widthMM - pad - 22, y, { align: "right" });
    doc.text(asMoney(it.unit_price), widthMM - pad - 12, y, { align: "right" });
    doc.text(asMoney(amount), widthMM - pad, y, { align: "right" });
    y += Math.max(3, lines.length * 3);
  }
  dashed();

  // Totals
  const total = Number(p.total || 0);
  const sub = Number(p.subtotal || 0);
  const disc = Number(p.discount || p.extra_discount || 0);
  const gst = Number(p.tax || 0);

  doc.setFontSize(7);
  doc.text("Subtotal", pad, y);
  doc.text(asMoney(sub), widthMM - pad, y, { align: "right" });
  y += 3;
  if (disc > 0) {
    doc.text("Discount", pad, y);
    doc.text(`- ${asMoney(disc)}`, widthMM - pad, y, { align: "right" });
    y += 3;
  }
  if (showGst) {
    doc.text("GST (incl.)", pad, y);
    doc.text(asMoney(gst), widthMM - pad, y, { align: "right" });
    y += 3;
  }
  dashed();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(widthMM >= 80 ? 10 : 9);
  doc.text("TOTAL", pad, y);
  doc.text(asMoney(total), widthMM - pad, y, { align: "right" });
  y += 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(`Payment: ${payMethod}`, pad, y);
  y += 4;

  dashed();
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  const thanks = cfg?.thank_you || "Thank you! Visit again.";
  const thankLines = doc.splitTextToSize(thanks, inner);
  doc.text(thankLines, centerX, y, { align: "center" });
  y += thankLines.length * 3;
  if (cfg?.return_policy) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    const rLines = doc.splitTextToSize(`Returns: ${cfg.return_policy}`, inner);
    doc.text(rLines, centerX, y, { align: "center" });
    y += rLines.length * 2.5;
  }
  if (cfg?.footer) {
    const fLines = doc.splitTextToSize(cfg.footer, inner);
    doc.text(fLines, centerX, y, { align: "center" });
    y += fLines.length * 3;
  }
  if (cfg?.terms) {
    doc.setFontSize(6);
    const tLines = doc.splitTextToSize(cfg.terms, inner);
    doc.text(tLines, centerX, y, { align: "center" });
  }

  return doc;
}

export function buildInvoicePDF({ order, invoice, cfg, paperSize = "a4" }) {
  if (!order) throw new Error("order required to build PDF");
  const size = String(paperSize || "a4").toLowerCase();
  if (size === "58mm") return buildThermal(order, invoice, cfg || {}, 58);
  if (size === "80mm") return buildThermal(order, invoice, cfg || {}, 80);
  return buildA4(order, invoice, cfg || {});
}

// Convert to a File so navigator.share({ files: [...] }) can send the PDF itself.
export function pdfBlobToFile(blob, name) {
  return new File([blob], name, { type: "application/pdf" });
}

// Open a print dialog for the PDF (works on all modern browsers).
export function printPDF(doc) {
  const blobUrl = doc.output("bloburl");
  // Try iframe print (silent-ish, no popup blocker)
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = blobUrl;
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch {
      // Fallback: open new window
      window.open(blobUrl, "_blank");
    }
    // Clean up later so print dialog can render
    setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 60000);
  };
  document.body.appendChild(iframe);
}
